import React from 'react';
import styles from '../../styles/ReporteEstimacionAlumnadoStyles/ReporteEstimacionAlumnado.module.css';

/**
 * Tabla del Reporte de Estimación de Alumnado.
 *
 * Props:
 *  - programas: Array<{...}>
 *  - contexto: { ciclo, campus, seccion, programa, institucion }
 *  - estadisticas: { total_actual, total_nuevo_ingreso, total_reinscripcion,
 *                    total_reingreso, total_bajas,
 *                    pct_reinscripcion, pct_desercion, pct_total }
 */
function TablaReporte({ programas = [], contexto = {}, estadisticas = null }) {
  const totalGeneral = programas.reduce(
    (acc, prog) => {
      const t = prog.total_programa || {};
      acc.nuevo_ingreso += t.nuevo_ingreso || 0;
      acc.reingresos += t.reingresos || 0;
      acc.reinscripcion += t.reinscripcion || 0;
      acc.total_estimacion += t.total_estimacion || 0;
      return acc;
    },
    { nuevo_ingreso: 0, reingresos: 0, reinscripcion: 0, total_estimacion: 0 }
  );

  const NUM_COLS = 6;

  const stats = estadisticas || {};
  const hasStats = stats.total_actual != null;

  return (
    <>
      {/* ── Encabezado + Panel de estadísticas ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
        <div>
          <div className={styles.reportHeader} style={{ marginBottom: 4 }}>
            <h2 className={styles.reportTitle}>ESTIMACIÓN DE ALUMNADO</h2>
          </div>
          <div className={styles.reportMeta}>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Institución:</span>
              <span>{contexto.institucion || '—'}</span>
            </div>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Ciclo:</span>
              <span>{contexto.ciclo || '—'}</span>
            </div>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Campus:</span>
              <span>{contexto.campus || 'Todos'}</span>
            </div>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Sección:</span>
              <span>{contexto.seccion || 'Todas'}</span>
            </div>
            <div className={styles.reportMetaItem}>
              <span className={styles.reportMetaLabel}>Programa:</span>
              <span>{contexto.programa || 'Todos'}</span>
            </div>
          </div>
        </div>

        {/* ── Panel de estadísticas (esquina superior derecha) ── */}
        {hasStats && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 220,
            flexShrink: 0,
          }}>
            {[
              { label: '% Reinscripción', value: stats.pct_reinscripcion, color: '#1a6e3c', bg: '#e8f5e9', title: `${stats.total_reinscripcion + stats.total_reingreso} de ${(stats.total_reinscripcion + stats.total_reingreso + stats.total_bajas)} alumnos de ciclos anteriores regresaron` },
              { label: '% Deserción',     value: stats.pct_desercion,     color: '#b71c1c', bg: '#ffebee', title: `${stats.total_bajas} alumnos con baja activa (ciclos anteriores)` },
              { label: '% Total activos', value: stats.pct_total,         color: '#0d47a1', bg: '#e3f2fd', title: `${stats.total_actual} activos de ${stats.total_actual + stats.total_bajas} esperados` },
            ].map(({ label, value, color, bg, title }) => (
              <div key={label} title={title} style={{
                background: bg,
                border: `1px solid ${color}22`,
                borderRadius: 8,
                padding: '8px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                cursor: 'default',
              }}>
                <span style={{ fontSize: 12, color: '#444', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color }}>{value ?? 0}%</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: '#888', textAlign: 'right', paddingRight: 2 }}>
              Total alumnado activo: <strong style={{ color: '#333' }}>{stats.total_actual ?? 0}</strong>
            </div>
          </div>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: 220 }}>PROGRAMA</th>
              <th className={styles.cellCenter} style={{ minWidth: 110 }}>TETRAMESTRE</th>
              <th className={styles.cellCenter} style={{ minWidth: 110 }}>NUEVO INGRESO</th>
              <th className={styles.cellCenter} style={{ minWidth: 100 }}>REINGRESOS</th>
              <th className={styles.cellCenter} style={{ minWidth: 120 }}>REINSCRIPCIÓN</th>
              <th className={styles.cellCenter} style={{ minWidth: 130 }}>TOTAL ESTIMACIÓN</th>
            </tr>
          </thead>
          <tbody>
            {programas.length === 0 ? (
              <tr>
                <td
                  colSpan={NUM_COLS}
                  className={styles.cellCenter}
                  style={{ padding: '20px', color: '#999' }}
                >
                  No hay estudiantes para los filtros aplicados.
                </td>
              </tr>
            ) : (
              programas.map((prog, pi) => (
                <React.Fragment key={prog.id_programa ?? pi}>
                  {/* Filas por tetramestre */}
                  {(prog.filas || []).map((fila, fi) => (
                    <tr key={`${prog.id_programa}-${fila.tetramestre}-${fi}`}>
                      <td>{fi === 0 ? prog.nombre_programa : ''}</td>
                      <td className={styles.cellCenter}>{fila.tetramestre}</td>
                      <td className={styles.cellCenter}>{fila.nuevo_ingreso}</td>
                      <td className={styles.cellCenter}>{fila.reingresos}</td>
                      <td className={styles.cellCenter}>{fila.reinscripcion}</td>
                      <td className={styles.cellCenter}><strong>{fila.total_estimacion}</strong></td>
                    </tr>
                  ))}

                  {/* Fila TOTAL PROGRAMA */}
                  <tr className={styles.rowTotal}>
                    <td style={{ textAlign: 'right', paddingRight: 12 }}>TOTAL PROGRAMA</td>
                    <td className={styles.cellCenter}>{prog.total_programa?.nuevo_ingreso != null ? '' : ''}</td>
                    <td className={styles.cellCenter}>{prog.total_programa?.nuevo_ingreso ?? 0}</td>
                    <td className={styles.cellCenter}>{prog.total_programa?.reingresos ?? 0}</td>
                    <td className={styles.cellCenter}>{prog.total_programa?.reinscripcion ?? 0}</td>
                    <td className={styles.cellCenter}>{prog.total_programa?.total_estimacion ?? 0}</td>
                  </tr>

                  {/* Espaciador entre programas */}
                  {pi < programas.length - 1 && (
                    <tr className={styles.rowSpacer}>
                      <td colSpan={NUM_COLS} />
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}

            {/* Fila de total general si hay más de un programa */}
            {programas.length > 1 && (
              <>
                <tr className={styles.rowSpacer}>
                  <td colSpan={NUM_COLS} />
                </tr>
                <tr className={styles.rowTotal} style={{ borderTop: '2px solid #002f6c' }}>
                  <td colSpan={2} style={{ textAlign: 'right', paddingRight: 12 }}>
                    TOTAL GENERAL
                  </td>
                  <td className={styles.cellCenter}>{totalGeneral.nuevo_ingreso}</td>
                  <td className={styles.cellCenter}>{totalGeneral.reingresos}</td>
                  <td className={styles.cellCenter}>{totalGeneral.reinscripcion}</td>
                  <td className={styles.cellCenter}>{totalGeneral.total_estimacion}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TablaReporte;
